import React from 'react';
import { Stack, Typography, useTheme } from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { layoutGap } from '../../../theme/theme';
import HelpTooltipTrigger from './HelpTooltipTrigger';
import type { HelpValue } from './helpModel';

export interface SectionHeaderProps {
  label: string;
  help?: HelpValue;
  variant?: 'caption' | 'body2';
  action?: React.ReactNode;
  gutterBottom?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Reusable section header with an optional help tooltip and action slot.
 * Encapsulates the common label + tooltip pattern used across workspace panels
 * so individual features do not re-implement the same typography stack.
 */
const SectionHeader: React.FC<SectionHeaderProps> = ({
  label,
  help,
  variant = 'caption',
  action,
  gutterBottom = true,
  sx,
}) => {
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      spacing={layoutGap.dense}
      alignItems="center"
      sx={{ mb: gutterBottom ? 0.75 : undefined, ...sx }}
    >
      <Typography
        variant={variant}
        sx={{
          fontWeight: variant === 'caption' ? 800 : 700,
          lineHeight: 1.35,
          letterSpacing: 0,
          textTransform: 'none',
          color: variant === 'caption' ? 'text.secondary' : alpha(theme.palette.text.primary, 0.9),
        }}
      >
        {label}
      </Typography>
      {help ? <HelpTooltipTrigger label={label} help={help} placement="bottom-start" /> : null}
      {action ? <>{action}</> : null}
    </Stack>
  );
};

export default SectionHeader;
